import { nextTick } from "vue";

const SCROLL_RETRY_INTERVAL = 80;
const SCROLL_TOP_RETRIES = 12;
const SCROLL_HASH_RETRIES = 40;
const SCROLL_TOP_DELAYS = [0, 100, 250, 500, 900];

/** 首页导航菜单 path → 首页区块 id */
export const INDEX_HOME_ANCHOR_MAP = {
	"/dashboard/about": "about-wenbo",
	"/dashboard/products": "functional-wenbo",
	"/dashboard/business": "results-wenbo",
	"/dashboard/newsroom": "new-wenbo",
};

/**
 * 读取当前页面滚动距离（兼容 window / scrollingElement / html / body）
 */
export function getWindowScrollTop() {
	if (typeof window === "undefined") {
		return 0;
	}
	const scrollingEl = document.scrollingElement;
	return Math.max(
		0,
		window.scrollY || 0,
		window.pageYOffset || 0,
		(scrollingEl && scrollingEl.scrollTop) || 0,
		document.documentElement.scrollTop || 0,
		document.body.scrollTop || 0
	);
}

function getFixedHeaderOffset() {
	if (typeof document === "undefined") {
		return 0;
	}
	const header = document.querySelector(".el-header.fixed-top");
	if (!header) {
		return 0;
	}
	return Math.ceil(header.getBoundingClientRect().height) + 8;
}

function getHashTarget(hash) {
	if (!hash) {
		return null;
	}
	const id = hash.replace(/^#/, "");
	return document.getElementById(id) || document.querySelector(hash);
}

function scrollElementIntoView(el, behavior = "smooth", headerOffset = 0) {
	const offset = headerOffset > 0 ? headerOffset : getFixedHeaderOffset();
	const top = el.getBoundingClientRect().top + getWindowScrollTop() - offset;
	window.scrollTo({
		top: Math.max(0, top),
		left: 0,
		behavior,
	});
}

function isAnchorInView(el, headerOffset) {
	const offset = headerOffset > 0 ? headerOffset : getFixedHeaderOffset();
	const rect = el.getBoundingClientRect();
	return Math.abs(rect.top - offset) < 48;
}

/**
 * 滚动到 hash 对应锚点（异步组件加载后会重试；考虑固定顶栏偏移）
 */
export function scrollToHashTarget(hash, options = {}) {
	const {
		behavior = "smooth",
		retries = SCROLL_HASH_RETRIES,
		interval = SCROLL_RETRY_INTERVAL,
		headerOffset = 0,
	} = options;

	return new Promise((resolve) => {
		if (!hash || typeof window === "undefined") {
			resolve(false);
			return;
		}

		const attempt = (left, useInstant) => {
			const el = getHashTarget(hash);
			if (el) {
				const scrollBehavior = useInstant ? "auto" : behavior;
				scrollElementIntoView(el, scrollBehavior, headerOffset);

				requestAnimationFrame(() => {
					if (isAnchorInView(el, headerOffset) || left <= 0) {
						resolve(true);
						return;
					}
					setTimeout(() => attempt(left - 1, true), interval);
				});
				return;
			}
			if (left > 0) {
				setTimeout(() => attempt(left - 1, false), interval);
			} else {
				resolve(false);
			}
		};

		nextTick(() => {
			requestAnimationFrame(() => attempt(retries, false));
		});
	});
}

/**
 * 滚动窗口到顶部
 */
export function scrollWindowToTop(behavior = "auto") {
	if (typeof window === "undefined") {
		return;
	}
	window.scrollTo({ top: 0, left: 0, behavior });
	const scrollingEl = document.scrollingElement;
	if (scrollingEl) {
		scrollingEl.scrollTop = 0;
	}
	document.documentElement.scrollTop = 0;
	document.body.scrollTop = 0;
}

/**
 * 进入无 hash 的路由时回到顶部
 */
export function scrollPageToTop(options = {}) {
	const {
		behavior = "auto",
		retries = SCROLL_TOP_RETRIES,
		interval = SCROLL_RETRY_INTERVAL,
	} = options;

	return new Promise((resolve) => {
		if (typeof window === "undefined") {
			resolve();
			return;
		}

		const attempt = (left) => {
			scrollWindowToTop(behavior);
			if (left <= 0) {
				resolve();
				return;
			}
			setTimeout(() => attempt(left - 1), interval);
		};

		nextTick(() => {
			requestAnimationFrame(() => attempt(retries));
		});
	});
}

export function scheduleScrollToTop(options = {}) {
	const { delays = SCROLL_TOP_DELAYS, behavior = "auto" } = options;

	if (typeof window === "undefined") {
		return Promise.resolve();
	}

	scrollWindowToTop(behavior);

	const tasks = delays.map(
		(delay) =>
			new Promise((resolve) => {
				setTimeout(() => {
					scrollPageToTop({ behavior, retries: 4, interval: 50 }).then(resolve);
				}, delay);
			})
	);

	return Promise.all(tasks);
}

/**
 * 根据路由决定：有 hash 滚锚点，否则回顶部
 */
export function applyRouteScroll(to, from) {
	if (to.hash) {
		return scrollToHashTarget(to.hash, {
			behavior: "smooth",
			retries: to.name === "index" ? SCROLL_HASH_RETRIES : SCROLL_HASH_RETRIES - 8,
		}).then((ok) => {
			if (!ok) {
				return scheduleScrollToTop();
			}
		});
	}

	const pathChanged = !from || to.path !== from.path;
	if (pathChanged) {
		return scheduleScrollToTop();
	}

	return scrollPageToTop({ behavior: "auto" });
}

/**
 * 首页内锚点跳转（PC 顶栏 / 异步首页组件）
 */
export function scrollToIndexHash(hash, router) {
	const normalized = hash.startsWith("#") ? hash : `#${hash}`;
	const scroll = () =>
		scrollToHashTarget(normalized, {
			behavior: "smooth",
			retries: SCROLL_HASH_RETRIES,
			interval: SCROLL_RETRY_INTERVAL,
		});

	if (!router) {
		return scroll();
	}

	const current = router.currentRoute.value;
	if (current.name !== "index") {
		return router.push({ name: "index", hash: normalized }).then(scroll);
	}

	if (current.hash === normalized) {
		return scroll();
	}

	return router
		.push({ name: "index", hash: normalized })
		.then(scroll)
		.catch(() => scroll());
}
